<div class="cg-admin-page roles-permissions-page">
  <app-admin-page-header>
    <ng-container description>
      Configure menu access and action levels for one role at a time. Saving updates only the selected role.
    </ng-container>
  </app-admin-page-header>

  <div class="roles-permissions-page__toolbar" role="tablist" aria-label="Platform roles">
    @for (role of roles; track role.key) {
      <button
        type="button"
        class="roles-permissions-page__role-tab"
        role="tab"
        [attr.aria-selected]="selectedRoleKey() === role.key"
        [class.is-active]="selectedRoleKey() === role.key"
        (click)="selectRole(role.key)"
      >
        {{ role.label }}
        @if (role.locked) {
          <span class="roles-permissions-page__badge">Locked</span>
        }
      </button>
    }
    <div class="roles-permissions-page__role-meta">
      <span class="cg-form-help">
        Editing <strong>{{ selectedRoleLabel() }}</strong>
        @if (isLocked()) {
          <span> · full access (read-only)</span>
        }
      </span>
      @if (dirty() && !isLocked()) {
        <span class="roles-permissions-page__dirty" aria-live="polite">Unsaved changes</span>
      }
    </div>
  </div>

  @if (error()) {
    <p class="cg-form-error" role="alert">{{ error() }}</p>
  }

  @if (loading()) {
    <p class="cg-form-help">Loading permissions…</p>
  } @else {
    <div
      class="roles-permissions-page__view-tabs"
      role="tablist"
      aria-label="Permission areas"
    >
      <button
        type="button"
        class="roles-permissions-page__view-tab"
        role="tab"
        id="rp-tab-modules"
        [attr.aria-selected]="activeViewTab() === 'modules'"
        [attr.aria-controls]="'rp-panel-modules'"
        [class.is-active]="activeViewTab() === 'modules'"
        (click)="activeViewTab.set('modules')"
      >
        Module Access
      </button>
      <button
        type="button"
        class="roles-permissions-page__view-tab"
        role="tab"
        id="rp-tab-actions"
        [attr.aria-selected]="activeViewTab() === 'actions'"
        [attr.aria-controls]="'rp-panel-actions'"
        [class.is-active]="activeViewTab() === 'actions'"
        (click)="activeViewTab.set('actions')"
      >
        Action Permissions
      </button>
    </div>

    @if (activeViewTab() === 'modules') {
      <section
        class="roles-permissions-page__section"
        id="rp-panel-modules"
        role="tabpanel"
        aria-labelledby="rp-tab-modules"
      >
        <div class="roles-permissions-page__section-head">
          <p class="cg-form-help">
            Expand groups to configure child modules. ON allows menu and route access and sets actions to Execute (or Read Only when Execute is not available).
          </p>
          <div class="roles-permissions-page__section-tools">
            <input
              class="cg-input roles-permissions-page__search"
              type="search"
              placeholder="Filter…"
              [ngModel]="search()"
              (ngModelChange)="search.set($event)"
              aria-label="Filter modules"
            />
            <button type="button" class="roles-permissions-page__link-btn" (click)="expandAllModules()">
              Expand all
            </button>
            <button type="button" class="roles-permissions-page__link-btn" (click)="collapseAllModules()">
              Collapse all
            </button>
          </div>
        </div>

        <app-admin-table>
          <thead>
            <tr>
              <th>Module</th>
              <th class="roles-permissions-page__col-access">Access</th>
            </tr>
          </thead>
          <tbody>
            @for (row of moduleTreeRows(); track trackModuleRow(row)) {
              @if (row.kind === 'group') {
                <tr class="roles-permissions-page__tree-parent">
                  <td colspan="2">
                    <button
                      type="button"
                      class="roles-permissions-page__tree-toggle"
                      [attr.aria-expanded]="row.expanded"
                      (click)="toggleModuleGroup(row.groupKey)"
                    >
                      <app-admin-icon
                        class="roles-permissions-page__chevron"
                        [name]="row.expanded ? 'chevron-down' : 'chevron-right'"
                        [size]="16"
                      />
                      <span class="roles-permissions-page__tree-label">{{ row.label }}</span>
                      <span class="roles-permissions-page__count">{{ row.childCount }}</span>
                    </button>
                  </td>
                </tr>
              } @else {
                <tr [class.roles-permissions-page__tree-child]="row.indent">
                  <td>
                    <span
                      class="roles-permissions-page__module-name"
                      [class.is-child]="row.indent"
                      [class.is-root]="!row.indent"
                    >
                      @if (row.indent) {
                        <span class="roles-permissions-page__indent-spacer" aria-hidden="true"></span>
                      }
                      {{ row.label }}
                      @if (row.accessOnly) {
                        <span class="roles-permissions-page__hint">access only</span>
                      }
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="roles-permissions-page__switch"
                      [class.is-on]="isAccessOn(row.moduleKey)"
                      [attr.aria-pressed]="isAccessOn(row.moduleKey)"
                      [attr.aria-label]="'Access for ' + row.label"
                      [disabled]="isLocked()"
                      (click)="setAccess(row.moduleKey, !isAccessOn(row.moduleKey))"
                    >
                      <span class="roles-permissions-page__switch-label">
                        {{ isAccessOn(row.moduleKey) ? 'ON' : 'OFF' }}
                      </span>
                      <span class="roles-permissions-page__switch-knob" aria-hidden="true"></span>
                    </button>
                  </td>
                </tr>
              }
            } @empty {
              <tr>
                <td colspan="2" class="cg-form-help">No modules match your filter.</td>
              </tr>
            }
          </tbody>
        </app-admin-table>
      </section>
    } @else {
      <section
        class="roles-permissions-page__section"
        id="rp-panel-actions"
        role="tabpanel"
        aria-labelledby="rp-tab-actions"
      >
        <div class="roles-permissions-page__section-head">
          <p class="cg-form-help">
            Modules with Access ON. Expand a module to set No Access / Read Only / Execute per action.
          </p>
          <div class="roles-permissions-page__section-tools">
            <input
              class="cg-input roles-permissions-page__search"
              type="search"
              placeholder="Filter…"
              [ngModel]="search()"
              (ngModelChange)="search.set($event)"
              aria-label="Filter actions"
            />
            <button type="button" class="roles-permissions-page__link-btn" (click)="expandAllActions()">
              Expand all
            </button>
            <button type="button" class="roles-permissions-page__link-btn" (click)="collapseAllActions()">
              Collapse all
            </button>
          </div>
        </div>

        @if (actionTreeRows().length === 0) {
          <p class="cg-form-help roles-permissions-page__empty">
            No modules with Access ON have configurable actions.
          </p>
        } @else {
          <app-admin-table>
            <thead>
              <tr>
                <th>Module / Action</th>
                <th class="roles-permissions-page__col-level">Permission level</th>
              </tr>
            </thead>
            <tbody>
              @for (row of actionTreeRows(); track trackActionRow(row)) {
                @if (row.kind === 'module') {
                  <tr class="roles-permissions-page__tree-parent roles-permissions-page__tree-parent--module">
                    <td colspan="2">
                      <button
                        type="button"
                        class="roles-permissions-page__tree-toggle"
                        [attr.aria-expanded]="row.expanded"
                        (click)="toggleActionModule(row.moduleKey)"
                      >
                        <app-admin-icon
                          class="roles-permissions-page__chevron"
                          [name]="row.expanded ? 'chevron-down' : 'chevron-right'"
                          [size]="16"
                        />
                        <span class="roles-permissions-page__tree-label roles-permissions-page__tree-label--module">
                          {{ row.label }}
                        </span>
                        <span class="roles-permissions-page__count">{{ row.actionCount }}</span>
                      </button>
                    </td>
                  </tr>
                } @else {
                  <tr class="roles-permissions-page__tree-child">
                    <td>
                      <span class="roles-permissions-page__module-name is-child">
                        <span class="roles-permissions-page__indent-spacer" aria-hidden="true"></span>
                        {{ row.actionLabel }}
                      </span>
                    </td>
                    <td>
                      <select
                        class="cg-select roles-permissions-page__level-select"
                        [disabled]="isLocked()"
                        [ngModel]="getLevel(row.moduleKey, row.actionKey)"
                        (ngModelChange)="setLevel(row.moduleKey, row.actionKey, $event)"
                        [attr.aria-label]="row.actionLabel + ' permission level'"
                      >
                        @for (level of row.allowedLevels; track level) {
                          <option [value]="level">{{ levelLabel(level) }}</option>
                        }
                      </select>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </app-admin-table>
        }
      </section>
    }

    <div class="roles-permissions-page__spacer" aria-hidden="true"></div>

    <footer class="roles-permissions-page__sticky-bar">
      <div class="roles-permissions-page__sticky-bar-inner">
        <div class="roles-permissions-page__sticky-status">
          @if (isLocked()) {
            <span class="cg-form-help">Super Admin permissions cannot be modified.</span>
          } @else if (dirty()) {
            <span class="roles-permissions-page__dirty">Unsaved changes for {{ selectedRoleLabel() }}</span>
          } @else {
            <span class="cg-form-help">No unsaved changes</span>
          }
        </div>
        @if (showEdit()) {
          <div class="roles-permissions-page__sticky-actions">
            <app-admin-button
              variant="secondary"
              type="button"
              [disabled]="loading() || saving() || !dirty()"
              (click)="cancel()"
            >
              Cancel
            </app-admin-button>
            <app-admin-button
              variant="primary"
              type="button"
              [disabled]="!canEdit() || isLocked() || loading() || saving() || !dirty()"
              (click)="save()"
            >
              {{ saving() ? 'Saving…' : 'Save' }}
            </app-admin-button>
          </div>
        }
      </div>
    </footer>
  }
</div>
